import { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";

const DISMISSED_KEY = "ctc_pwa_dismissed_at";
const DISMISS_DAYS = 7;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 86400 * 1000;
  } catch {
    return false;
  }
}
function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 900;
}

export default function InstallAppBanner() {
  const [deferred, setDeferred] = useState(null);
  const [showAndroidBar, setShowAndroidBar] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isRecentlyDismissed()) return;

    // Android / desktop Chromium: browser fires beforeinstallprompt
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShowAndroidBar(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari: no install event exists — show a hint after a short delay
    // on mobile viewports only, so we don't spam desktop users.
    let iosTimer = null;
    if (isIOS() && isMobileViewport()) {
      iosTimer = setTimeout(() => setShowIOSHint(true), 4000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {}
    setShowAndroidBar(false);
    setShowIOSHint(false);
    setDeferred(null);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {}
    dismiss();
  };

  if (showAndroidBar && deferred) {
    return (
      <div
        data-testid="pwa-install-banner"
        className="fixed inset-x-3 bottom-3 z-50 md:left-auto md:right-4 md:max-w-md
                   bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-800
                   px-4 py-3 flex items-center gap-3"
        role="dialog"
        aria-live="polite"
      >
        <div className="flex-none w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center">
          <Download className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Install CTC Permits</div>
          <div className="text-[11px] text-slate-400">Fast access from your home screen.</div>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900"
        >
          Install
        </button>
        <button
          onClick={dismiss}
          data-testid="pwa-dismiss-btn"
          className="flex-none text-slate-400 hover:text-white p-1"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (showIOSHint) {
    return (
      <div
        data-testid="pwa-ios-hint"
        className="fixed inset-x-3 bottom-3 z-50 md:left-auto md:right-4 md:max-w-md
                   bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-800
                   px-4 py-3"
        role="dialog"
      >
        <div className="flex items-start gap-3">
          <div className="flex-none w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 flex items-center justify-center">
            <Share className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold">Install on iPhone</div>
            <div className="mt-0.5 text-[12px] text-slate-300 leading-snug">
              Tap the <Share className="inline w-3.5 h-3.5 align-text-bottom text-emerald-300" /> Share button below, then <em className="not-italic font-semibold text-white">Add to Home Screen</em>.
            </div>
          </div>
          <button
            onClick={dismiss}
            data-testid="pwa-ios-dismiss-btn"
            className="flex-none text-slate-400 hover:text-white p-1"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
